import React from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AnimatedSection from '@/components/AnimatedSection';
import styles from './About.module.scss';

const coreValues = [
  { num: '01', title: 'BUILD WITH PURPOSE', desc: 'Technology should solve a real problem.' },
  { num: '02', title: 'DESIGN WITH INTENT', desc: 'Every interface, interaction, and experience should have a reason.' },
  { num: '03', title: 'KEEP IT CLEAR', desc: "Good technology shouldn't need unnecessary complexity." },
  { num: '04', title: 'THINK BEYOND LAUNCH', desc: "A product isn't finished when it goes live." },
  { num: '05', title: 'GROW TOGETHER', desc: "The relationship doesn't end when the project ships." },
];


const About: React.FC = () => {
  return (
    <IonPage id="about-page">
      <IonContent fullscreen scrollY scrollEvents={true}>
        <div className="page-wrapper">
          <Navbar />

          {/* 1. HERO - WHO WE ARE */}
          {/* 1. HERO - WHO WE ARE */}
          <section className={`${styles.hero} dark-section`}>
            {/* Subtle background glow and grid */}
            <div className={styles.heroVisuals}>
              <div className={styles.heroGrid} />
              <div className={styles.heroGlow} />
            </div>

            <div className={`container ${styles.heroContainer}`}>
              <div className={styles.heroContent}>
                <AnimatedSection delay={0}>
                  <span className={styles.eyebrow}>ABOUT ARKATECHS</span>
                </AnimatedSection>
                
                <AnimatedSection delay={0.08}>
                  <h1 className={styles.heroTitle}>
                    We build digital products that <span className={styles.gradient}>move businesses forward.</span>
                  </h1>
                </AnimatedSection>
                
                <AnimatedSection delay={0.16}>
                  <p className={styles.heroSub}>
                    Arkatechs is a technology and digital solutions company building modern digital products, experiences, and growth systems for businesses worldwide.
                  </p>
                </AnimatedSection>
                
                <AnimatedSection delay={0.24}>
                  <div className={styles.heroEditorial}>
                    <span className={styles.metaItem}>EST. 2022</span>
                    <span className={styles.metaDivider}>—</span>
                    <span className={styles.metaItem}>INDIA · WORLDWIDE</span>
                    <span className={styles.metaDivider}>—</span>
                    <span className={styles.metaItem}>TECHNOLOGY & DIGITAL SOLUTIONS</span>
                  </div>
                </AnimatedSection>
              </div>
            </div>

            {/* Thin gradient separator line underneath hero */}
            <div className={styles.heroBottomLine} />
          </section>

          {/* 2. MISSION SPLIT */}
          <section className={`${styles.missionSection} light-section`}>
            <div className={`container ${styles.missionContainer}`}>
              <div className={styles.missionLeft}>
                <AnimatedSection>
                  <span className={styles.sectionLabelDark}>OUR MISSION</span>
                  <h2 className={styles.missionHeading}>
                    Turn ambitious ideas into<br />
                    meaningful digital products.
                  </h2>
                </AnimatedSection>
              </div>
              
              <div className={styles.missionRight}>
                <AnimatedSection delay={0.1}>
                  <p className={styles.missionText}>
                    We combine thoughtful design, reliable technology, and practical digital strategy to turn ideas into digital products that create real value.
                  </p>
                </AnimatedSection>
                
                <AnimatedSection delay={0.2}>
                  <div className={styles.compactProcess}>
                    <span>IDEA</span>
                    <span className={styles.processArrow}>→</span>
                    <span>DESIGN</span>
                    <span className={styles.processArrow}>→</span>
                    <span>BUILD</span>
                    <span className={styles.processArrow}>→</span>
                    <span className={styles.processAccent}>GROW</span>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </section>

          {/* 3. WHY ARKATECHS */}
          <section className={`${styles.whySection} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <span className={styles.sectionLabelLight}>OUR VISION</span>
              </AnimatedSection>
              
              <AnimatedSection delay={0.1}>
                <div className={styles.whyTop}>
                  <h2 className={styles.whyHeading}>
                    Build technology<br />that matters.
                  </h2>
                  <p className={styles.whyText}>
                    To build a technology company that creates products, platforms, and digital experiences capable of making a global impact.
                  </p>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <div className={styles.equation}>
                  <div className={styles.eqTerm}>STRATEGY</div>
                  <div className={styles.eqPlus}>+</div>
                  <div className={styles.eqTerm}>DESIGN</div>
                  <div className={styles.eqPlus}>+</div>
                  <div className={styles.eqTerm}>ENGINEERING</div>
                  <div className={styles.eqEqual}>=</div>
                  <div className={styles.eqResult}>IMPACT</div>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* 4. CORE VALUES */}
          <section className={`${styles.valuesSection} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <span className={styles.sectionLabelLight}>WHAT DRIVES US</span>
                <h2 className={styles.sectionHeadingLight}>Our Principles</h2>
              </AnimatedSection>
              
              <div className={styles.valuesList}>
                {coreValues.map((val, i) => (
                  <AnimatedSection key={val.num} delay={i * 0.1}>
                    <div className={`${styles.valueRow} ${i % 2 !== 0 ? styles.shiftRight : ''}`}>
                      <span className={styles.valueNum}>{val.num}</span>
                      <div className={styles.valueContent}>
                        <h3>{val.title}</h3>
                        <p>"{val.desc}"</p>
                      </div>
                      <ArrowRight className={styles.valueIcon} />
                      <div className={styles.valueLine} />
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </section>

          {/* 5. TEAM SHOWCASE (EDITORIAL ASYMMETRIC COMPOSITION) */}
          <section className={`${styles.teamSection} light-section`}>
            <div className="container">
              {/* Editorial Header Row with Subtle Meta Stamp */}
              <div className={styles.teamHeaderRow}>
                <AnimatedSection>
                  <span className={styles.sectionLabelDark}>THE TEAM</span>
                  <h2 className={styles.teamHeading}>
                    People behind<br />the work.
                  </h2>
                </AnimatedSection>
                <AnimatedSection delay={0.15}>
                  <div className={styles.editorialMetaTag} aria-hidden="true">
                    <span>ARKATECHS / 2022 — PRESENT</span>
                  </div>
                </AnimatedSection>
              </div>

              {/* Asymmetrical Editorial Composition */}
              <div className={styles.editorialDuo}>
                {/* Arman Khan - Left, Higher */}
                <div className={styles.editorialPersonLeft}>
                  <AnimatedSection delay={0.1}>
                    <div className={styles.portraitWrap}>
                      <img
                        src="/images/arman-khan.jpg"
                        alt="Arman Khan — Founder"
                        className={styles.editorialImage}
                      />
                    </div>
                    <div className={styles.editorialCaption}>
                      <h3 className={styles.personName}>ARMAN KHAN</h3>
                      <p className={styles.personRole}>FOUNDER</p>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Arshad Khan - Right, Lower */}
                <div className={styles.editorialPersonRight}>
                  <AnimatedSection delay={0.22}>
                    <div className={styles.portraitWrap}>
                      <img
                        src="/images/arshad-khan.jpg"
                        alt="Arshad Khan — Management & Technology"
                        className={styles.editorialImage}
                      />
                    </div>
                    <div className={styles.editorialCaption}>
                      <h3 className={styles.personName}>ARSHAD KHAN</h3>
                      <p className={styles.personRole}>MANAGEMENT & TECHNOLOGY</p>
                    </div>
                  </AnimatedSection>
                </div>
              </div>

              {/* Small Understated Statement */}
              <AnimatedSection delay={0.28}>
                <div className={styles.editorialStatement}>
                  <p className={styles.statementText}>
                    Two people. One direction. Built with technology, design, and intent.
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* 6. HOW WE THINK */}
          <section className={`${styles.thinkSection} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <span className={styles.sectionLabelLight}>HOW WE THINK</span>
                <h2 className={styles.thinkHeading}>
                  We don't chase technology.<br />
                  We use it with purpose.
                </h2>
              </AnimatedSection>
              
              <div className={styles.thinkPrinciples}>
                <AnimatedSection delay={0.1}>
                  <div className={styles.thinkItem}>
                    <span>01</span>
                    <h3>USEFUL OVER FLASHY</h3>
                  </div>
                </AnimatedSection>
                <AnimatedSection delay={0.2}>
                  <div className={styles.thinkItem}>
                    <span>02</span>
                    <h3>CLARITY OVER COMPLEXITY</h3>
                  </div>
                </AnimatedSection>
                <AnimatedSection delay={0.3}>
                  <div className={styles.thinkItem}>
                    <span>03</span>
                    <h3>LONG-TERM OVER QUICK WINS</h3>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </section>


          {/* 8. COMPANY INFO */}
          <section className={`${styles.whySection} dark-section`}>
            <div className="container">
              <AnimatedSection>
                <span className={styles.sectionLabelLight}>COMPANY</span>
              </AnimatedSection>
              
              <AnimatedSection delay={0.1}>
                <div className={styles.whyTop}>
                  <h2 className={styles.whyHeading}>
                    Arkatechs IT Solutions<br />Pvt. Ltd.
                  </h2>
                  <p className={styles.whyText}>
                    Founded in 2022, Arkatechs IT Solutions Pvt. Ltd. brings development, design, technology, and digital growth services together to help businesses build and strengthen their digital presence.
                  </p>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <div className={styles.equation}>
                  <div className={styles.eqTerm}>BASED IN INDIA</div>
                  <div className={styles.eqPlus}>·</div>
                  <div className={styles.eqTerm}>WORKING WORLDWIDE</div>
                  <div className={styles.eqPlus}>·</div>
                  <div className={styles.eqTerm}>REMOTE-FIRST</div>
                </div>
              </AnimatedSection>
            </div>
          </section>

          {/* 9. FINAL CTA */}
          <section className={`${styles.ctaSection} dark-section`}>
            <div className={styles.ctaVisuals}>
              <div className={styles.ring1} />
              <div className={styles.ring2} />
              <div className={styles.ring3} />
            </div>
            
            <div className={`container ${styles.ctaContainer}`}>
              <AnimatedSection>
                <h2 className={styles.ctaHeading}>
                  Let's build<br />
                  <span className={styles.gradient}>something meaningful.</span>
                </h2>
              </AnimatedSection>
              
              <AnimatedSection delay={0.1}>
                <p className={styles.ctaSub}>
                  Tell us about your idea, project, or business challenge.
                </p>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <Link to="/contact" className={styles.ctaLink}>
                  START A CONVERSATION <ArrowRight size={18} />
                  <div className={styles.ctaLine} />
                </Link>
              </AnimatedSection>
            </div>
          </section>

          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default About;
